"use client";

import React, { createContext, useContext, ReactNode } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";

// 1. Define the shape of our context
interface LayoutContextType {
  isFullscreenMode: boolean;
  setIsFullscreenMode: (mode: boolean) => void;
}

// 2. Create the context. We initialize it with undefined to help us
// detect when the hook is used outside of a provider.
const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

// 3. The Provider component that holds the actual state.
// It now uses the URL as the single source of truth for fullscreen mode.
export const LayoutProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // The URL is the single source of truth for fullscreen mode.
  const isFullscreenMode = searchParams.get("isFullscreen") === "true";

  const setIsFullscreenMode = (mode: boolean) => {
    const params = new URLSearchParams(searchParams.toString());
    if (mode) {
      params.set("isFullscreen", "true");
    } else {
      params.delete("isFullscreen");
    }
    // Update the URL, which in turn updates the context via useSearchParams()
    router.replace(`${pathname}?${params.toString()}`);
  };

  return <LayoutContext.Provider value={{ isFullscreenMode, setIsFullscreenMode }}>{children}</LayoutContext.Provider>;
};

// 4. Custom hook to consume the context.
// This includes a safety check to ensure it's used within a provider.
export const useLayoutContext = () => {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error("useLayoutContext must be used within a LayoutProvider");
  }
  return context;
};
